import { onMounted, onUnmounted } from 'vue'

/**
 * Vue composable for adding and automatically cleaning up event listeners
 * Provides proper TypeScript support and SSR compatibility
 */
export function useEventListener<T extends keyof WindowEventMap>(
  target: Window,
  event: T,
  handler: (event: WindowEventMap[T]) => void,
  options?: boolean | AddEventListenerOptions,
): void

export function useEventListener<T extends keyof DocumentEventMap>(
  target: Document,
  event: T,
  handler: (event: DocumentEventMap[T]) => void,
  options?: boolean | AddEventListenerOptions,
): void

export function useEventListener<T extends keyof HTMLElementEventMap>(
  target: HTMLElement,
  event: T,
  handler: (event: HTMLElementEventMap[T]) => void,
  options?: boolean | AddEventListenerOptions,
): void

// Generic overload for custom events
export function useEventListener(
  target: Window | Document | HTMLElement,
  event: string,
  handler: (event: Event) => void,
  options?: boolean | AddEventListenerOptions,
): void

export function useEventListener(
  target: Window | Document | HTMLElement,
  event: string,
  handler: (event: Event) => void,
  options?: boolean | AddEventListenerOptions,
): void {
  // Only add listeners in browser environment
  if (typeof window === 'undefined') return

  onMounted(() => {
    target.addEventListener(event, handler, options)
  })

  onUnmounted(() => {
    target.removeEventListener(event, handler, options)
  })
}
